import type { ChapterPlan, Ledger, RoadmapPlanningState, RoadmapWarning, StoryBible } from '../types';
import {
  allRequiredChunksPresent,
  assembleRoadmapFromChunks,
  getMissingChunkRanges,
  getRoadmapChunkRanges,
  logRoadmapPlanningDeveloperError,
  mergeRoadmapPlanningState,
  normalizeRoadmapChunkPayload,
  RoadmapChunkRange,
  RoadmapPlanningError,
  upsertRoadmapChunk,
  validateAssembledRoadmap,
} from './roadmapPlanning.service';

export interface RoadmapChunkGenerationResult {
  rawText: string;
  requestId?: string;
}

export interface RunRoadmapPlanningParams {
  bible: StoryBible;
  ledger: Ledger;
  onProgress?: (message: string) => void;
  onStateChange?: (state: RoadmapPlanningState) => void | Promise<void>;
  parseChunkResponse: (rawText: string) => unknown;
  generateChunk: (input: {
    range: RoadmapChunkRange;
    previousPlans: ChapterPlan[];
  }) => Promise<RoadmapChunkGenerationResult>;
}

export interface RoadmapPlanningResult {
  plans: ChapterPlan[];
  warnings: RoadmapWarning[];
  planningState: RoadmapPlanningState;
}

async function persistState(
  state: RoadmapPlanningState,
  onStateChange?: (state: RoadmapPlanningState) => void | Promise<void>,
): Promise<void> {
  if (onStateChange) {
    await onStateChange({
      ...state,
      updatedAt: new Date().toISOString(),
    });
  }
}

export async function runRoadmapPlanning(params: RunRoadmapPlanningParams): Promise<RoadmapPlanningResult> {
  const { bible, onProgress, onStateChange, parseChunkResponse, generateChunk } = params;
  const targetChapterCount = bible.targetChapterCount;
  const ranges = getRoadmapChunkRanges(targetChapterCount);
  let state = mergeRoadmapPlanningState(bible.roadmapPlanning, targetChapterCount);

  try {
    const missingRanges = getMissingChunkRanges(state, ranges);
    let previousPlans = assembleRoadmapFromChunks(state.chunks, targetChapterCount);

    for (const range of missingRanges) {
      if (onProgress) {
        onProgress(`Synthesizing Roadmap chunk: Chapters ${range.startChapter} to ${range.endChapter}...`);
      }

      const chunkResult = await generateChunk({ range, previousPlans });
      if (!chunkResult || typeof chunkResult.rawText !== 'string') {
        throw new RoadmapPlanningError(
          `Roadmap chunk ${range.chunkIndex} (${range.startChapter}-${range.endChapter}) returned no model payload.`,
          {
            chunkIndex: range.chunkIndex,
            chapterRange: `${range.startChapter}-${range.endChapter}`,
            missingField: 'rawText',
          },
        );
      }
      const { rawText, requestId } = chunkResult;
      let parsed: unknown;
      try {
        parsed = parseChunkResponse(rawText || '{}');
      } catch (error: unknown) {
        throw new RoadmapPlanningError(
          `Roadmap chunk ${range.chunkIndex} (${range.startChapter}-${range.endChapter}) returned malformed JSON.`,
          {
            chunkIndex: range.chunkIndex,
            chapterRange: `${range.startChapter}-${range.endChapter}`,
            missingField: 'json',
            requestId,
            developerMessage: error instanceof Error ? error.message : String(error),
          },
          error,
        );
      }

      const chunkRecord = normalizeRoadmapChunkPayload(parsed, range, requestId);
      state = upsertRoadmapChunk(state, chunkRecord);
      await persistState(state, onStateChange);
      previousPlans = assembleRoadmapFromChunks(state.chunks, targetChapterCount);
    }

    if (!allRequiredChunksPresent(state, ranges)) {
      throw new RoadmapPlanningError('Roadmap planning is incomplete after chunk generation.', {
        developerMessage: `Missing chunk indexes: ${getMissingChunkRanges(state, ranges)
          .map((range) => range.chunkIndex)
          .join(', ')}`,
      });
    }

    state = {
      ...state,
      phase: 'assembling',
      updatedAt: new Date().toISOString(),
    };
    await persistState(state, onStateChange);

    const plans = assembleRoadmapFromChunks(state.chunks, targetChapterCount);
    const warnings = validateAssembledRoadmap(plans, targetChapterCount);

    state = {
      ...state,
      phase: 'complete',
      lastError: undefined,
      updatedAt: new Date().toISOString(),
    };
    await persistState(state, onStateChange);

    return { plans, warnings, planningState: state };
  } catch (error: unknown) {
    logRoadmapPlanningDeveloperError(error);
    state = {
      ...state,
      phase: 'failed',
      lastError: error instanceof RoadmapPlanningError ? error.message : error instanceof Error ? error.message : String(error),
      updatedAt: new Date().toISOString(),
    };
    await persistState(state, onStateChange);
    throw error;
  }
}

export async function runRoadmapAssemblyOnly(
  bible: StoryBible,
  onStateChange?: (state: RoadmapPlanningState) => void | Promise<void>,
): Promise<RoadmapPlanningResult> {
  const ranges = getRoadmapChunkRanges(bible.targetChapterCount);
  let state = mergeRoadmapPlanningState(bible.roadmapPlanning, bible.targetChapterCount);
  if (!allRequiredChunksPresent(state, ranges)) {
    throw new RoadmapPlanningError('Cannot assemble roadmap because one or more chunks are missing or invalid.', {
      developerMessage: `Missing chunks: ${getMissingChunkRanges(state, ranges)
        .map((range) => range.chunkIndex)
        .join(', ')}`,
    });
  }

  state = { ...state, phase: 'assembling', updatedAt: new Date().toISOString() };
  await persistState(state, onStateChange);
  const plans = assembleRoadmapFromChunks(state.chunks, bible.targetChapterCount);
  const warnings = validateAssembledRoadmap(plans, bible.targetChapterCount);
  state = { ...state, phase: 'complete', lastError: undefined, updatedAt: new Date().toISOString() };
  await persistState(state, onStateChange);
  return { plans, warnings, planningState: state };
}
